import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import FinancePanel from "@/components/FinancePanel";
import IssuesPanel from "@/components/IssuesPanel";
import TasksPanel from "@/components/TasksPanel";
import WeatherPanel from "@/components/WeatherPanel";
import AppSurface from "@/components/AppSurface";
import FinanceCalendarPanel from "@/components/FinanceCalendarPanel";
import AnimatedSystemMap from "@/components/AnimatedSystemMap";
import MyJourneyPanel from "@/components/MyJourneyPanel";
import AgentStudio from "@/components/AgentStudio";
import AgentBlueprint from "@/components/AgentBlueprint";
import SocialComposer from "@/components/SocialComposer";
import ArchitectureDiagram from "@/components/ArchitectureDiagram";
import CommandCenter from "@/components/CommandCenter";
import ChatHistoryPanel from "@/components/ChatHistoryPanel";
import CommandsCheatSheet from "@/components/CommandsCheatSheet";
import RawHubDashboard from "@/components/RawHubDashboard";
import CapitalPnLPanel from "@/components/CapitalPnLPanel";
import OvernightPanel from "@/components/OvernightPanel";
import SettingsPanel from "@/components/SettingsPanel";
import OpenTradesPanel from "@/components/OpenTradesPanel";
import YearHeatmapPanel from "@/components/YearHeatmapPanel";

const HEADLINE_STYLE = "font-display text-2xl rc-glow-text mb-3 tracking-widest";

/**
 * BriefCapitalLiveStrip — Capital live numbers inside the morning_brief
 * panel that REFRESH every 5 seconds. Owner asked the brief card to keep
 * ticking with live balance / unrealised P&L / positions instead of being
 * frozen on the snapshot taken when the brief was generated.
 */
function BriefCapitalLiveStrip({ initialCap }) {
  const [cap, setCap] = useState(initialCap || {});
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get("/jarvis/capital_pnl");
        const d = r.data || {};
        if (!alive) return;
        const acctBal = d.account?.balance;
        // Capital.com's account.balance is itself an object {balance, available, deposit}
        // — pulling the raw object into Number() gives NaN on the panel.
        const balanceNum = (acctBal && typeof acctBal === "object")
          ? Number(acctBal.balance)
          : Number(acctBal);
        setCap((prev) => ({
          enabled: true,
          balance_gbp: Number.isFinite(balanceNum) ? balanceNum : prev.balance_gbp,
          unrealised_pnl_gbp: d.open_upl_gbp ?? prev.unrealised_pnl_gbp,
          positions: (d.open_positions || []).map((p) => ({
            symbol: p.market?.instrumentName || p.market?.epic || "—",
            direction: p.position?.direction,
            upl: p.position?.upl ?? 0,
          })),
        }));
      } catch {}
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return (
    <div className="border-2 border-[var(--rc-gold)] p-3 mb-3" data-testid="brief-capital">
      <div className="rc-label text-[9px] mb-1.5">CAPITAL.COM LIVE · <span style={{ color: "var(--rc-gold)" }}>● TICKING</span></div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[9px] opacity-60">BALANCE</div>
          <div className="font-display text-base rc-glow-text tabular-nums">£{Number(cap.balance_gbp || 0).toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[9px] opacity-60">UNREALISED</div>
          <div className={`font-display text-base tabular-nums ${(cap.unrealised_pnl_gbp ?? 0) >= 0 ? "text-green-400" : "text-red-400"}`}>£{Number(cap.unrealised_pnl_gbp || 0).toFixed(2)}</div>
        </div>
        <div>
          <div className="text-[9px] opacity-60">POSITIONS</div>
          <div className="font-display text-base mt-0.5">{(cap.positions || []).map(p => `${p.symbol} ${p.direction}`).join(", ") || "flat"}</div>
        </div>
      </div>
    </div>
  );
}

function HowItWorksStats({ fallback }) {
  const [j, setJ] = useState(fallback || {});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/jarvis/journey");
        if (alive && r.data) setJ((prev) => ({ ...prev, ...r.data }));
      } catch {}
    })();
    return () => { alive = false; };
  }, []);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="how-i-work-stats">
      <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">APPS</div><div className="font-display text-xl rc-glow-text tabular-nums mt-1">{j.apps_built ?? 0}</div></div>
      <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">CONVOS</div><div className="font-display text-xl rc-glow-text tabular-nums mt-1">{(j.conversations ?? j.conversations_total ?? 0).toLocaleString()}</div></div>
      <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">SPEND</div><div className="font-display text-xl rc-glow-text tabular-nums mt-1">£{(j.spend_gbp ?? 0).toLocaleString()}</div></div>
      <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">API KEYS</div><div className="font-display text-xl rc-glow-text tabular-nums mt-1">{j.api_keys ?? j.api_keys_total ?? 0}</div></div>
      <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">WEBHOOKS</div><div className="font-display text-xl rc-glow-text tabular-nums mt-1">{j.webhooks ?? j.webhooks_total ?? 0}</div></div>
    </div>
  );
}

function ProactiveSwarmBanner({ severity, flags, question }) {
  const [opened, setOpened] = useState(false);
  // Auto-fire ONE time, 5s after the brief renders, only if owner doesn't
  // dismiss first. Gives them a moment to read flags before swarm pops.
  useEffect(() => {
    if (opened) return;
    const t = setTimeout(() => {
      if (!opened) {
        window.dispatchEvent(new CustomEvent("rawco-open-swarm", { detail: { message: question } }));
        setOpened(true);
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [question, opened]);
  const color = severity === "high" ? "#ff5252" : "#ffb800";
  return (
    <div className="mt-3 p-3 border rounded animate-pulse"
      style={{ borderColor: color, background: `${color}11` }}
      data-testid="proactive-swarm-banner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="rc-label text-[10px] mb-1" style={{ color }}>⚠ PROACTIVE — RED FLAGS DETECTED</div>
          <div className="text-[11px] opacity-90 mb-1">{flags.length} concern{flags.length > 1 ? "s" : ""}:</div>
          <ul className="text-[10px] opacity-80 list-disc list-inside space-y-0.5">
            {flags.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setOpened(true);
              window.dispatchEvent(new CustomEvent("rawco-open-swarm", { detail: { message: question } }));
            }}
            className="rc-btn !text-[10px] !py-1.5"
            data-testid="proactive-swarm-fire"
          >ASK THE SWARM NOW</button>
          <button
            onClick={() => setOpened(true)}
            className="border border-[var(--rc-gold-dim)] text-[var(--rc-text-dim)] px-3 py-1.5 text-[10px] tracking-widest opacity-70 hover:opacity-100"
            data-testid="proactive-swarm-dismiss"
          >DISMISS</button>
        </div>
      </div>
      {opened && <div className="text-[10px] opacity-60 mt-2 italic">✓ Swarm summoned. They're on it.</div>}
    </div>
  );
}



export default function StageVisual({ visual, dismiss, onChoice, onPin, onSend }) {
  // Esc-key closes the panel
  useEffect(() => {
    if (!visual) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visual, dismiss]);

  // ⏱ Auto-dismiss the CLARIFY card after 120 seconds — owner complained
  // the 30s timer dismissed the card before he could think and answer by
  // voice ("I replied by voice and said a ghost story but he didn't carry
  // on"). 2 minutes gives plenty of time to read the options and answer.
  // If the owner picks an option or hits Esc before then, the parent
  // unmounts us and this timer is cleaned up by the unmount effect.
  useEffect(() => {
    if (!visual || visual.type !== "clarify") return;
    if (visual.pinned) return; // respect the 📌 pin
    const t = setTimeout(() => { try { dismiss(); } catch {} }, 120000);
    return () => clearTimeout(t);
  }, [visual, dismiss]);

  if (!visual) return null;
  const { type, action } = visual;
  const result = action?.result || {};

  const close = (
    <div className="absolute top-2 right-2 z-30 flex gap-1">
      {onPin && !visual.pinned && (
        <button
          onClick={onPin}
          className="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center text-base font-display text-[var(--rc-gold)] border border-[var(--rc-gold-dim)] hover:bg-[var(--rc-gold)] hover:text-black active:bg-[var(--rc-gold)] active:text-black transition-colors"
          data-testid="stage-pin"
          aria-label="Pin panel"
          title="Pin this panel so it stays visible (📌)"
        >📌</button>
      )}
      <button
        onClick={dismiss}
        className="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center text-xl font-display text-[var(--rc-gold)] border border-[var(--rc-gold)] bg-[rgba(0,4,12,0.85)] hover:bg-[var(--rc-gold)] hover:text-black active:bg-[var(--rc-gold)] active:text-black transition-colors"
        data-testid="stage-dismiss"
        aria-label="Close panel"
        title="Close panel (Esc)"
      >×</button>
    </div>
  );

  // App-specific visual
  if (type === "open_app") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid={`stage-app-${action.app_key}`}>
        {close}
        <div className={HEADLINE_STYLE}>APP · {(action.app_key || "").toUpperCase()}</div>
        <div className="h-[520px]"><AppSurface appKey={action.app_key} refreshKey={Date.now()} /></div>
      </div>
    );
  }

  if (type === "get_analytics") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-4xl w-full" data-testid="stage-analytics">
        {close}
        <div className={HEADLINE_STYLE}>// APP ANALYTICS</div>
        <div className="h-[480px]"><AnalyticsPanel refreshKey={Date.now()} /></div>
      </div>
    );
  }

  if (type === "get_finance") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-finance">
        {close}
        <div className={HEADLINE_STYLE}>// INTAKE · OUTGOINGS</div>
        <div className="h-[500px]"><FinancePanel refreshKey={Date.now()} /></div>
      </div>
    );
  }

  if (type === "get_trades" || type === "place_trade" || type === "close_trade" || type === "close_all_trades") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-trades">
        {close}
        <div className={HEADLINE_STYLE}>// CAPITAL · TRADES {result.trade_mode ? `· ${result.trade_mode.toUpperCase()}` : ""}</div>
        {result.is_mock && (
          <div
            className="mb-3 border-2 border-red-500/70 bg-red-500/10 px-3 py-2 text-xs tracking-widest font-display rc-blink"
            data-testid="trade-mock-banner"
          >
            ⚠ DEMO MOCK — NOT A REAL TRADE. Capital.com {result.broker === "mock" && !result.broker_order_id ? "credentials missing or order rejected" : "fallback engaged"}.
          </div>
        )}
        {result.symbol && result.entry_price && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">SYMBOL</div><div className="font-display text-base rc-glow-text mt-1">{result.symbol}</div></div>
            <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">SIDE</div><div className="font-display text-base mt-1">{(action.side || result.side || "").toUpperCase()}</div></div>
            <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">CAPITAL</div><div className="font-display text-base rc-glow-text mt-1">£{action.capital || result.capital}</div></div>
            <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">ENTRY</div><div className="font-display text-base mt-1 tabular-nums">{result.entry_price}</div></div>
            <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">STOP</div><div className="font-display text-base mt-1 tabular-nums">{result.stop || result.stop_price || "—"}</div></div>
            <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">BROKER</div><div className="font-display text-base mt-1">{result.broker || "—"}</div></div>
          </div>
        )}
      </div>
    );
  }

  if (type === "get_weather") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-md w-full" data-testid="stage-weather">
        {close}
        <div className={HEADLINE_STYLE}>// ATMOSPHERIC</div>
        <div className="h-[260px]"><WeatherPanel refreshKey={Date.now()} /></div>
      </div>
    );
  }

  if (type === "read_news") {
    const items = result.items || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-news">
        {close}
        <div className={HEADLINE_STYLE}>// {(result.category || "TOP").toUpperCase()} · BBC NEWS</div>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {items.map((it, i) => (
            <a key={i} href={it.link} target="_blank" rel="noreferrer" className="block border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] p-3 transition-colors">
              <div className="font-display text-sm">{it.title}</div>
              <div className="text-xs text-[var(--rc-text-dim)] mt-1">{it.summary?.slice(0, 200)}</div>
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (type === "ebay_search") {
    const items = result.items || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-ebay">
        {close}
        <div className={HEADLINE_STYLE}>// EBAY · {(result.query || "").toUpperCase()}</div>
        <a href={result.search_url} target="_blank" rel="noreferrer" className="rc-btn inline-block mb-3 !text-[10px]">OPEN ON EBAY ↗</a>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {items.length === 0 && <div className="rc-label opacity-60">Opening eBay search in a new tab...</div>}
          {items.map((it, i) => (
            <a key={i} href={it.link} target="_blank" rel="noreferrer" className="flex gap-3 border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] p-2">
              {it.image && <img src={it.image} alt="" className="w-16 h-16 object-cover" />}
              <div className="flex-1">
                <div className="font-display text-xs">{it.title}</div>
                <div className="rc-glow-text text-sm mt-1">{it.price_text}</div>
                {it.location && <div className="text-[10px] text-[var(--rc-text-dim)]">{it.location}</div>}
              </div>
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (type === "weather_diagnose") {
    // 🌤 Weather diagnostic — owner: "why is weather always offline?"
    // Step-by-step trace so the failure point is obvious.
    const steps = result.steps || [];
    const verdict = result.verdict || "";
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-weather-diagnose">
        {close}
        <div className={HEADLINE_STYLE}>// WEATHER DIAGNOSTIC</div>
        <div className="mt-3 space-y-2">
          {steps.map((s, i) => (
            <div key={i} className="flex items-start gap-3 py-1.5 border-b border-[var(--rc-gold-dim)]/30" data-testid={`wx-step-${s.step}`}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: s.ok ? "#10b981" : "#ef4444", marginTop: 5 }}/>
              <div className="flex-1">
                <div className="text-xs">{s.step.replaceAll("_", " ")}</div>
                {s.error && <div className="text-[10px] text-red-400 mt-0.5">{s.error}</div>}
                {s.value && <div className="text-[10px] text-[var(--rc-text-dim)] mt-0.5">→ {s.value}</div>}
                {s.geo && <div className="text-[10px] text-[var(--rc-text-dim)] mt-0.5">→ {s.geo.name} ({s.geo.lat}, {s.geo.lon})</div>}
                {s.temp_c !== undefined && <div className="text-[10px] text-[var(--rc-text-dim)] mt-0.5">→ {s.temp_c}°C</div>}
              </div>
            </div>
          ))}
        </div>
        {verdict && (
          <div className="mt-4 p-3 border border-[var(--rc-gold-dim)] text-[11px]" data-testid="wx-verdict">
            <div className="rc-glow-text text-[10px] mb-1">// VERDICT</div>
            {verdict}
          </div>
        )}
      </div>
    );
  }


  if (type === "camera_health") {
    // 🩺 Camera health diagnostic — owner: "I haven't seen the camera
    // work ONCE". Surfaces in plain English whether the ESP32 is alive,
    // stale, or never connected — and what to fix.
    const status = result.status;
    const colour = status === "LIVE" ? "var(--rc-gold)" : status === "STALE" ? "#FFA500" : "#FF4D4D";
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-camera-health">
        {close}
        <div className={HEADLINE_STYLE}>// CAMERA HEALTH</div>
        <div className="mt-3 flex items-center gap-3">
          <div style={{ width: 14, height: 14, borderRadius: "50%", background: colour, boxShadow: `0 0 12px ${colour}` }}/>
          <div className="text-sm font-semibold" style={{ color: colour }}>{status}</div>
        </div>
        <div className="text-xs mt-2">{result.summary}</div>
        {result.last_uploaded_at && (
          <div className="text-[10px] text-[var(--rc-text-dim)] mt-2">
            Last frame: {new Date(result.last_uploaded_at).toLocaleString()}<br/>
            Device: {result.device_id}
          </div>
        )}
        {result.fix && (
          <div className="mt-3 p-3 border border-[var(--rc-gold-dim)] text-[11px] text-[var(--rc-text-dim)]" data-testid="camera-health-fix">
            <div className="rc-glow-text text-[10px] mb-1">// FIX</div>
            {result.fix}
          </div>
        )}
      </div>
    );
  }


  if (type === "agent_activity") {
    // 📜 Agent Activity Log — owner: "add agent activity log so I can see
    // what they did and when". Shows newest auto-executed swarm actions.
    const entries = result.entries || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-agent-activity">
        {close}
        <div className={HEADLINE_STYLE}>// AGENT ACTIVITY · LAST {entries.length}</div>
        {entries.length === 0 ? (
          <div className="text-xs text-[var(--rc-text-dim)] mt-3">No agent activity yet, sir. Once Tom, Sarah and the team start auto-running actions, they'll show up here.</div>
        ) : (
          <div className="mt-3 max-h-[65vh] overflow-y-auto divide-y divide-[var(--rc-gold-dim)]/30">
            {entries.map((e, i) => (
              <div key={i} className="py-2.5 flex items-start gap-3" data-testid={`activity-row-${i}`}>
                <div className="text-[10px] text-[var(--rc-text-dim)] w-28 shrink-0 mt-0.5">
                  {new Date(e.ts).toLocaleString()}
                </div>
                <div className="flex-1">
                  <div className="text-xs">
                    <span className="rc-glow-text font-semibold">{e.agent}</span>
                    {" "}{e.ok ? "ran" : <span className="text-red-400">tried</span>}{" "}
                    <span className="opacity-80">{e.label}</span>
                  </div>
                  {e.trigger_message && (
                    <div className="text-[10px] text-[var(--rc-text-dim)] mt-0.5 italic">"{e.trigger_message}"</div>
                  )}
                  {!e.ok && e.error && (
                    <div className="text-[10px] text-red-400 mt-0.5">{e.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (type === "live_eye") {
    // 👁 Live Eye Feed — owner: "the camera takes 2 days to wire in and
    // I haven't seen it work once". Shows the most-recent ESP32 frame.
    if (result.error === "no_frame") {
      return (
        <div className="stage-in rc-panel relative p-5 max-w-xl w-full" data-testid="stage-live-eye-empty">
          {close}
          <div className={HEADLINE_STYLE}>// LIVE EYE · NO FEED</div>
          <div className="text-xs text-[var(--rc-text-dim)] mt-3">
            No frame uploaded yet. Check the ESP32-S3 is powered, on Wi-Fi, and pushing to <code className="text-[var(--rc-gold)]">/api/jarvis/camera/upload</code>.
          </div>
        </div>
      );
    }
    const src = result.image_base64?.startsWith("data:") ? result.image_base64 : `data:image/jpeg;base64,${result.image_base64}`;
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-live-eye">
        {close}
        <div className={HEADLINE_STYLE}>// LIVE EYE · {(result.device_id || "ESP32").toUpperCase()}</div>
        <img src={src} alt="Live ESP32 feed" className="w-full mt-3 border border-[var(--rc-gold-dim)]"
             style={{ maxHeight: "70vh", objectFit: "contain" }} />
        <div className="text-[10px] text-[var(--rc-text-dim)] mt-2">
          Uploaded {result.uploaded_at ? new Date(result.uploaded_at).toLocaleString() : "—"} · auto-refreshes when you re-ask
        </div>
      </div>
    );
  }


  if (type === "ask_grok") {
    // 🤖 Grok (xAI) answer — owner says "ask Grok …". Real-time X/Twitter
    // knowledge. Backend returns {question, answer, model} or {error}.
    if (result.error) {
      return (
        <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-grok-error">
          {close}
          <div className={HEADLINE_STYLE}>// GROK · UNAVAILABLE</div>
          <div className="text-xs text-[var(--rc-text-dim)] mt-3">{result.error}</div>
          {result.question && <div className="text-[10px] opacity-50 mt-3 italic">Q: &ldquo;{result.question}&rdquo;</div>}
        </div>
      );
    }
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-grok">
        {close}
        <div className={HEADLINE_STYLE}>// GROK · {(result.model || "xAI").toUpperCase()}</div>
        <div className="text-[10px] opacity-60 mt-2 italic">Q: &ldquo;{result.question}&rdquo;</div>
        <div className="mt-3 text-sm whitespace-pre-wrap max-h-[60vh] overflow-y-auto" data-testid="grok-answer">{result.answer}</div>
      </div>
    );
  }

  if (type === "generate_image") {
    // 🍌 Owner asked for AI-generated cinematic images via Nano Banana
    // (Gemini 3 image gen). Backend `generate_image` action returns
    // images: [{mime_type, data_url}] where data_url is a base64 PNG/JPEG.
    // Render full-size with a download button.
    const images = result.images || [];
    if (!images.length) {
      return (
        <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-gen-image-empty">
          {close}
          <div className={HEADLINE_STYLE}>// AI IMAGE · BLOCKED</div>
          <div className="text-xs text-[var(--rc-text-dim)] mt-3">{result.error || "Gemini didn't return an image — the prompt may have been blocked."}</div>
          {result.prompt && <div className="text-[10px] opacity-50 mt-3 italic">&ldquo;{result.prompt}&rdquo;</div>}
        </div>
      );
    }
    return (
      <div className="stage-in rc-panel relative p-5 max-w-4xl w-full" data-testid="stage-gen-image">
        {close}
        <div className={HEADLINE_STYLE}>// AI IMAGE · NANO BANANA</div>
        <div className="text-[10px] opacity-60 mt-1 italic line-clamp-2">&ldquo;{result.prompt || action.prompt}&rdquo;</div>
        <div className="grid grid-cols-1 gap-3 mt-3 max-h-[75vh] overflow-y-auto">
          {images.map((img, i) => (
            <div key={i} className="border border-[var(--rc-gold-dim)]" data-testid={`gen-image-${i}`}>
              <img
                src={img.data_url}
                alt={result.prompt || "Generated image"}
                style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", display: "block" }}
              />
              <a
                href={img.data_url}
                download={`raw-data-image-${Date.now()}.${(img.mime_type || "image/png").split("/")[1] || "png"}`}
                className="block text-center text-[10px] py-1.5 rc-label hover:rc-glow-text border-t border-[var(--rc-gold-dim)]"
                data-testid={`gen-image-download-${i}`}
              >⬇ DOWNLOAD</a>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "show_image") {
    // Owner asked: "show me a Ferrari" / "pull up a picture of X" — backend
    // returns up to 6 images via Tavily (with DuckDuckGo fallback). Renders
    // them as a clean grid on the mirror. Owner complaint: "nothing gets
    // put onto the [mirror] screen" was caused by show_image being missing
    // from both VISUAL_ACTIONS and the renderer.
    const images = result.images || [];
    if (!images.length) {
      return (
        <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-show-image-empty">
          {close}
          <div className={HEADLINE_STYLE}>// IMAGE · {(result.query || "").toUpperCase()}</div>
          <div className="text-xs text-[var(--rc-text-dim)] mt-3">{result.error || "No images returned for that query, sir."}</div>
        </div>
      );
    }
    return (
      <div className="stage-in rc-panel relative p-5 max-w-5xl w-full" data-testid="stage-show-image">
        {close}
        <div className={HEADLINE_STYLE}>// IMAGE · {(result.query || "").toUpperCase()}</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 max-h-[70vh] overflow-y-auto">
          {images.map((img, i) => (
            <a
              key={i}
              href={img.url}
              target="_blank"
              rel="noreferrer"
              className="block border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] overflow-hidden"
              data-testid={`show-image-${i}`}
            >
              <img
                src={img.url}
                alt={img.description || result.query}
                loading="lazy"
                style={{ width: "100%", height: "200px", objectFit: "cover", display: "block" }}
                onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
              />
              {img.description && (
                <div className="text-[10px] text-[var(--rc-text-dim)] p-2 truncate">{img.description}</div>
              )}
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (type === "web_search") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-search">
        {close}
        <div className={HEADLINE_STYLE}>// LIVE WEB · TAVILY</div>
        <div className="text-xs text-[var(--rc-text-dim)] mb-3">Query: <span className="text-[var(--rc-gold)]">{result.query}</span></div>
        {result.answer && (
          <div className="border border-[var(--rc-gold)] p-3 mb-3">
            <div className="rc-label text-[9px] mb-1">AI ANSWER</div>
            <div className="text-sm">{result.answer}</div>
          </div>
        )}
        <div className="space-y-2 max-h-[40vh] overflow-y-auto">
          {(result.results || []).map((r, i) => (
            <a key={i} href={r.url} target="_blank" rel="noreferrer" className="block border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] p-2">
              <div className="font-display text-xs">{r.title}</div>
              <div className="text-[10px] text-[var(--rc-text-dim)] mt-1">{r.snippet}</div>
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (type === "get_revenue") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-revenue">
        {close}
        <div className={HEADLINE_STYLE}>// REVENUE · {result.from} → {result.to}</div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">ITEMS</div><div className="font-display text-2xl rc-glow-text mt-1 tabular-nums">{result.total_items}</div></div>
          <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">REVENUE</div><div className="font-display text-2xl rc-glow-text mt-1 tabular-nums">£{result.total_revenue_gbp}</div></div>
        </div>
        <div className="space-y-1 max-h-[35vh] overflow-y-auto">
          {(result.days || []).map(d => (
            <div key={d.date} className="flex justify-between text-xs border-b border-[var(--rc-gold-dim)] py-1">
              <span>{d.date}</span>
              <span className="tabular-nums">{d.qty} items · £{d.revenue}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "list_notes") {
    const notes = result.notes || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-notes">
        {close}
        <div className={HEADLINE_STYLE}>// YOUR NOTES</div>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {notes.map((n, i) => (
            <div key={i} className="border border-[var(--rc-gold-dim)] p-3">
              <div className="text-sm">{n.text}</div>
              <div className="rc-label text-[9px] mt-1">{n.created_at?.slice(0, 16)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "list_reminders" || type === "list_watches") {
    const items = result.reminders || result.watches || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid={`stage-${type}`}>
        {close}
        <div className={HEADLINE_STYLE}>// {type === "list_watches" ? "WATCHES" : "REMINDERS"}</div>
        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {items.map((r, i) => (
            <div key={i} className="border border-[var(--rc-gold-dim)] p-3">
              <div className="text-sm">{r.text || r.topic}</div>
              <div className="rc-label text-[9px] mt-1">{r.trigger_at?.slice(0, 16) || r.condition}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "daily_summary") {
    const tasks = result.tasks || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-daily">
        {close}
        <div className={HEADLINE_STYLE}>// TODAY · {result.count} TASKS</div>
        <div className="space-y-1 max-h-[55vh] overflow-y-auto">
          {tasks.map((t, i) => (
            <div key={i} className="border-l-2 border-[var(--rc-gold)] pl-3 py-1">
              <div className="font-display text-xs">{t.title}</div>
              <div className="text-[10px] text-[var(--rc-text-dim)]">{t.detail}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "list_voices") {
    const voices = result.voices || [];
    const active = result.active;
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-voices">
        {close}
        <div className={HEADLINE_STYLE}>// VOICE GALLERY · {voices.length}</div>
        <div className="text-[10px] rc-label mb-3 text-[var(--rc-text-dim)]">Click PREVIEW to hear · click USE to make it my voice. (Or just say "use the [name] voice".)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto pr-1">
          {voices.map((v) => {
            const isActive = v.voice_id === active;
            return (
              <div
                key={v.voice_id}
                data-testid={`voice-${v.voice_id}`}
                className={`border p-3 ${isActive ? "border-[var(--rc-gold)] rc-glow-text" : "border-[var(--rc-gold-dim)]"}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="font-display text-sm flex-1">{v.name} {isActive && <span className="text-[9px] rc-label ml-1">· ACTIVE</span>}</div>
                  <div className="flex gap-1 shrink-0">
                    {v.preview_url && (
                      <button
                        data-testid={`voice-preview-${v.voice_id}`}
                        onClick={() => { const a = new Audio(v.preview_url); a.play().catch(()=>{}); }}
                        className="rc-btn !text-[9px] !py-1 !px-2"
                        title="Hear a sample of this voice"
                      >▶ PREVIEW</button>
                    )}
                    {/* USE button — always visible (disabled when this
                        IS the active voice). Owner brief Feb 2026: "the
                        only thing opens is click preview to hear … haven't
                        actually pulling up the settings so I can change
                        his voice like it used to" — was previously
                        conditionally hidden which is why the owner
                        couldn't see how to switch. */}
                    <button
                      data-testid={`voice-use-${v.voice_id}`}
                      disabled={isActive}
                      onClick={async () => {
                        if (isActive) return;
                        try {
                          const tk = localStorage.getItem("rawco_token");
                          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/set_voice`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
                            body: JSON.stringify({ voice_id: v.voice_id, name: v.name }),
                          });
                          const confirmText = `Voice switched to ${v.name}, sir.`;
                          const tts = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/tts`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
                            body: JSON.stringify({ text: confirmText }),
                          });
                          const tjson = await tts.json();
                          if (tjson?.audio) {
                            const a = new Audio(tjson.audio);
                            a.play().catch(() => {});
                          }
                          dismiss?.();
                          setTimeout(() => {
                            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/chat`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
                              body: JSON.stringify({ message: "show me the voice gallery", session_id: localStorage.getItem("rawco_session_id") || "default" }),
                            });
                          }, 800);
                        } catch (e) {
                          console.warn("voice switch failed", e);
                        }
                      }}
                      className="!text-[10px] !py-1 !px-3 font-bold tracking-widest transition-all"
                      style={{
                        background: isActive ? "rgba(100,116,139,0.15)" : "var(--rc-gold)",
                        color: isActive ? "var(--rc-text-dim)" : "#000",
                        border: `1.5px solid ${isActive ? "var(--rc-gold-dim)" : "var(--rc-gold)"}`,
                        cursor: isActive ? "default" : "pointer",
                        boxShadow: isActive ? "none" : "0 0 8px rgba(255,184,0,0.5)",
                      }}
                      title={isActive ? "Already active" : `Make ${v.name} my voice`}
                    >{isActive ? "✓ ACTIVE" : "USE"}</button>
                  </div>
                </div>
                <div className="text-[10px] text-[var(--rc-text-dim)] mt-1 tracking-wide">{v.category} · {Object.values(v.labels || {}).filter(Boolean).join(" · ")}</div>
                {v.description && <div className="text-[10px] text-[var(--rc-text-dim)] mt-1">{v.description}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "set_voice") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-md w-full" data-testid="stage-set-voice">
        {close}
        <div className={HEADLINE_STYLE}>// VOICE SWITCHED</div>
        <div className="border border-[var(--rc-gold)] p-4">
          <div className="rc-label text-[10px]">NOW SPEAKING AS</div>
          <div className="font-display text-2xl rc-glow-text mt-2">{result.name || action.name || "Updated"}</div>
          <div className="text-[10px] text-[var(--rc-text-dim)] mt-2">voice_id · {result.voice_id}</div>
        </div>
      </div>
    );
  }

  if (type === "open_calendar") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-calendar">
        {close}
        <div className={HEADLINE_STYLE}>// CALENDAR · £ DAILY</div>
        <FinanceCalendarPanel data={result} />
      </div>
    );
  }

  if (type === "get_trades") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-open-trades">
        {close}
        <div className={HEADLINE_STYLE}>// OPEN POSITIONS</div>
        <OpenTradesPanel data={result} />
      </div>
    );
  }

  if (type === "show_commands") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-6xl w-full" data-testid="stage-commands">
        {close}
        <div className="flex items-center justify-between mb-4">
          <div className={HEADLINE_STYLE}>// VOICE COMMANDS · CHEAT SHEET</div>
          <div className="rc-label text-[9px] opacity-60">EVERYTHING · YOU · CAN · ASK · ME</div>
        </div>
        <CommandsCheatSheet data={result} />
      </div>
    );
  }

  if (type === "my_journey") {
    return (
      <div className="stage-in rc-panel relative max-w-5xl w-full p-0" data-testid="stage-my-journey">
        {close}
        <MyJourneyPanel data={result} />
      </div>
    );
  }
  if (type === "agent_studio") {
    return (
      <div className="stage-in rc-panel relative max-w-6xl w-full p-0" data-testid="stage-agent-studio">
        {close}
        <AgentStudio dismiss={dismiss} />
      </div>
    );
  }
  if (type === "open_year_heatmap") {
    return (
      <div className="stage-in rc-panel relative p-4 max-w-6xl w-full" data-testid="stage-year-heatmap">
        {close}
        <YearHeatmapPanel data={result} />
      </div>
    );
  }
  if (type === "agent_blueprint") {
    return (
      <div className="stage-in rc-panel relative max-w-6xl w-full p-5" data-testid="stage-agent-blueprint">
        {close}
        <AgentBlueprint stack={result.stack} />
      </div>
    );
  }
  if (type === "social_composer") {
    return (
      <div className="stage-in rc-panel relative max-w-3xl w-full p-0" data-testid="stage-social-composer">
        {close}
        <SocialComposer dismiss={dismiss} />
      </div>
    );
  }
  if (type === "toms_workshop") {
    const requests = result?.requests || [];
    const approve = async (req) => {
      const prompt = `Hey Emergent main agent — Tom's learning mode flagged that "${req.trigger_title}" has happened ${req.repeat_count} times this week. ${req.summary}\n\nPlease propose and ship a permanent fix.`;
      try { await navigator.clipboard.writeText(prompt); } catch {}
      try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/app_change_requests/${req.id}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("rawco_token")}` },
        body: JSON.stringify({ status: "approved" }),
      }); } catch {}
      window.alert("✓ Approval saved + fix request copied to clipboard. Paste it into the Emergent main chat.");
      dismiss?.();
    };
    const reject = async (req) => {
      try { await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/app_change_requests/${req.id}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("rawco_token")}` },
        body: JSON.stringify({ status: "rejected" }),
      }); } catch {}
      dismiss?.();
    };
    return (
      <div className="stage-in rc-panel relative p-4 max-w-3xl w-full" data-testid="stage-toms-workshop">
        {close}
        <div className={HEADLINE_STYLE + " mb-3"}>// TOM'S WORKSHOP</div>
        <div className="text-[11px] opacity-70 mb-4">
          Tom's been watching for patterns. When the same issue keeps coming back, he proposes a permanent fix.
        </div>
        {requests.length === 0 && (
          <div className="text-center py-8 opacity-60 text-[11px]" data-testid="toms-workshop-empty">
            // No proposals right now. Tom hasn't spotted any repeating patterns.
            <br />Check back after a few days of activity.
          </div>
        )}
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.id} className="border border-[var(--rc-gold-dim)] p-3" data-testid={`toms-workshop-card-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="rc-label text-[10px] opacity-80">🛠 {r.proposed_by?.toUpperCase()}</div>
                <div className="text-[10px] opacity-60">{r.repeat_count}× in 7 days</div>
              </div>
              <div className="font-display rc-glow-text text-sm mb-1">{r.trigger_title}</div>
              <div className="text-[11px] opacity-80 mb-3">{r.summary}</div>
              <div className="flex gap-2">
                <button onClick={() => approve(r)} className="rc-btn !text-[10px] !py-1.5 flex-1" data-testid="toms-workshop-approve">
                  ✅ APPROVE & ASK MAIN AGENT
                </button>
                <button onClick={() => reject(r)} className="border border-[var(--rc-gold-dim)] text-[var(--rc-text-dim)] px-3 py-1.5 text-[10px] tracking-widest opacity-70 hover:opacity-100" data-testid="toms-workshop-reject">
                  REJECT
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (type === "show_architecture") {
    const j = result?.journey || {};
    const features = result?.features || [];
    const appsPinned = result?.apps_pinned || [];
    // Clusters that map directly to a real agent → clicking them opens
    // the Agent Studio with that agent pre-selected so the owner can
    // edit voice, persona, prompt, guardrails all in one place. Owner
    // asked: "make the diagram clickable so I can edit each agent"
    const agentClusters = {
      "TOM":   { name: "Tom",   note: "Engineer · debugging · fixes" },
      "SARAH": { name: "Sarah", note: "Social · content · captions" },
      "EVA":   { name: "Eva",   note: "Concierge · settings · admin" },
      "SCOUT": { name: "Scout", note: "Recon · web search · OSINT" },
      "DOBBY": { name: "Dobby", note: "Janitor · cleanup · maintenance" },
    };
    const clusters = [
      { name: "BRAIN",         tone: "gold",    note: "GPT-5.2 + Claude 4.5 + Gemini 3 Pro + ElevenLabs",
        action: { type: "studio" } },
      { name: "ORCHESTRATOR",  tone: "purple",  note: "intent router · action map · SwarmChat",
        action: { type: "studio" } },
      { name: "AGENTS · 5 VOICES", tone: "mint", note: "Tom · Sarah · Eva · Scout · Dobby (each ElevenLabs voice)",
        action: { type: "studio" } },
      { name: "TOM",           tone: "blue",    note: agentClusters.TOM.note,   action: { type: "studio", agent: "Tom" } },
      { name: "SARAH",         tone: "coral",   note: agentClusters.SARAH.note, action: { type: "studio", agent: "Sarah" } },
      { name: "EVA",           tone: "magenta", note: agentClusters.EVA.note,   action: { type: "studio", agent: "Eva" } },
      { name: "SCOUT",         tone: "lime",    note: agentClusters.SCOUT.note, action: { type: "studio", agent: "Scout" } },
      { name: "DOBBY",         tone: "yellow",  note: agentClusters.DOBBY.note, action: { type: "studio", agent: "Dobby" } },
      { name: "SWARM TOOL-USE", tone: "lime",   note: `${(result?.features?.length || 0) >= 1 ? "FIX IT buttons · whitelisted actions" : "tool-use ready"}` },
      { name: "PROACTIVE",     tone: "orange",  note: "morning brief auto-fires swarm on red flags" },
      { name: "CHANNELS",      tone: "magenta", note: "Voice · SwarmChat · Slack · iMessage · ESP32 wake" },
      { name: "INTEGRATIONS",  tone: "yellow",  note: `${j.services_armed || 0}/${j.services_total || 0} services armed`,
        action: { type: "settings", tab: "integrations" } },
      { name: "DATA · CLOUD",  tone: "cyan",    note: "Gmail · Calendar · Drive · GitHub · Ring" },
      { name: "SOCIAL",        tone: "coral",   note: "Instagram · TikTok · YouTube · Facebook · Resend" },
      { name: "MY APPS",       tone: "gold",    note: `${j.apps || appsPinned.length} apps wired in & pinnable as widgets`,
        action: { type: "settings", tab: "apps" } },
      { name: "WIDGETS",       tone: "mint",    note: `${j.widgets_pinned || 0} pinned · live weather/users/iframes`,
        action: { type: "settings", tab: "widgets" } },
      { name: "AUTO-MAINT",    tone: "purple",  note: "04:00 UTC daily synthetic-issue purge cron" },
    ];
    const handleClusterClick = (c) => {
      if (!c.action) return;
      if (c.action.type === "studio") {
        // Dispatch global event picked up by App.js → opens Agent Studio modal
        window.dispatchEvent(new CustomEvent("rawco-open-agent-studio", {
          detail: { agent: c.action.agent || null }
        }));
      } else if (c.action.type === "settings") {
        window.dispatchEvent(new CustomEvent("rawco-open-settings", {
          detail: { tab: c.action.tab || "general" }
        }));
      }
    };
    return (
      <div className="stage-in rc-panel relative p-4 max-w-5xl w-full" data-testid="stage-architecture">
        {close}
        <div className="flex items-center justify-between mb-3">
          <div className={HEADLINE_STYLE}>// RAW-DATA · SYSTEM MAP</div>
          <div className="rc-label text-[9px] opacity-60">LIVE · CONSTELLATION · TAP NODES</div>
        </div>
        <AnimatedSystemMap />
        {/* Cascade cluster rundown — animates in left-to-right after the map mounts */}
        <div className="sys-map-cascade" data-testid="sys-map-cascade">
          {clusters.map((c, i) => (
            <div
              key={c.name}
              role={c.action ? "button" : undefined}
              tabIndex={c.action ? 0 : undefined}
              onClick={() => handleClusterClick(c)}
              onKeyDown={(e) => { if (c.action && (e.key === "Enter" || e.key === " ")) handleClusterClick(c); }}
              className={`sys-map-cluster tone-${c.tone}${c.action ? " sys-map-cluster-clickable" : ""}`}
              style={{ animationDelay: `${0.45 + i * 0.08}s`, cursor: c.action ? "pointer" : "default" }}
              data-testid={`sys-map-cluster-${c.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
            >
              <span className="sys-map-cluster-dot" />
              <span className="sys-map-cluster-name">{c.name}</span>
              <span className="sys-map-cluster-note">{c.note}</span>
              {c.action && <span className="sys-map-cluster-chev" style={{ marginLeft: 6, opacity: 0.5 }}>›</span>}
            </div>
          ))}
        </div>
        {appsPinned.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--rc-gold-dim)]">
            <div className="rc-label text-[9px] opacity-70 mb-2">// APPS PINNABLE AS LIVE WIDGETS</div>
            <div className="flex flex-wrap gap-2">
              {appsPinned.map((a) => (
                <div key={a.name} className="border border-[var(--rc-gold-dim)] px-2 py-1 text-[10px]" data-testid={`arch-app-${a.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <span className="rc-glow-text">{a.name}</span>
                  <span className="opacity-50 ml-1">{a.url.replace(/^https?:\/\//, "")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {features.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--rc-gold-dim)]">
            <div className="rc-label text-[9px] opacity-70 mb-2">// RECENT FEATURE ADDITIONS</div>
            <ul className="text-[10px] opacity-80 list-disc list-inside space-y-0.5">
              {features.map((f, i) => <li key={i}>{f}</li>)}
            </ul>
          </div>
        )}
      </div>
    );
  }

  if (type === "command_center") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-7xl w-full" data-testid="stage-command-center">
        {close}
        <div className="flex items-center justify-between mb-4">
          <div className={HEADLINE_STYLE}>// COMMAND CENTER · {result.count || 0} APPS</div>
          <div className="rc-label text-[9px] opacity-60">ALL · BUSINESSES · ONE · GLANCE</div>
        </div>
        <CommandCenter data={result} />
      </div>
    );
  }

  if (type === "list_sessions") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-sessions">
        {close}
        <div className={HEADLINE_STYLE}>// CONVERSATION HISTORY</div>
        <ChatHistoryPanel data={result} mode="sessions" />
      </div>
    );
  }

  if (type === "recall_chats") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-recall">
        {close}
        <div className={HEADLINE_STYLE}>// MEMORY · RECALL</div>
        <ChatHistoryPanel data={result} mode="recall" />
      </div>
    );
  }

  if (type === "open_session") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-session-transcript">
        {close}
        <div className={HEADLINE_STYLE}>// TRANSCRIPT · {result.session_id?.slice(0, 8)}</div>
        <ChatHistoryPanel data={result} mode="session" />
      </div>
    );
  }

  if (type === "clarify") {
    const options = action.options || result.options || [];
    const question = action.question || result.question || "";
    return (
      <div className="stage-in rc-panel relative p-5 max-w-md w-full" data-testid="stage-clarify">
        {close}
        <div className={HEADLINE_STYLE}>// CLARIFY</div>
        <div className="border border-[var(--rc-gold)] p-4">
          {question && <div className="text-sm text-[var(--rc-text)] mb-3">{question}</div>}
          {options.length > 0 && (
            <div className="space-y-1.5">
              {options.map((opt, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onChoice?.(opt)}
                  className="w-full text-left border border-[var(--rc-gold-dim)] px-3 py-2 text-xs tracking-wide hover:border-[var(--rc-gold)] hover:rc-glow-text hover:bg-[var(--rc-gold)]/10 transition-all cursor-pointer"
                  data-testid={`clarify-option-${i}`}
                >
                  <span className="rc-label text-[9px] opacity-70 mr-2">{String.fromCharCode(65 + i)}</span>
                  {opt}
                </button>
              ))}
            </div>
          )}
          <div className="rc-label text-[9px] opacity-60 mt-3">CLICK OR JUST SAY WHICH ONE, SIR.</div>
        </div>
      </div>
    );
  }

  if (type === "search_flights") {
    const offers = result.offers || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-search_flights">
        {close}
        <div className={HEADLINE_STYLE}>// FLIGHTS · {result.origin} → {result.destination} · {result.depart_date}</div>
        {result.error && <div className="text-red-400 text-xs">⚠ {result.error}. {result.where || ""}</div>}
        <div className="space-y-1.5 mt-2">
          {offers.map((o, i) => (
            <div key={i} className="border border-[var(--rc-gold-dim)] p-2 flex justify-between text-xs" data-testid={`flight-${i}`}>
              <div>
                <div className="font-display">{o.carrier} · {o.stops === 0 ? "DIRECT" : `${o.stops} stop${o.stops > 1 ? "s" : ""}`}</div>
                <div className="opacity-70 text-[10px]">{o.depart?.slice(0, 16).replace("T", " ")} → {o.arrive?.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="font-display rc-glow-text text-base tabular-nums">£{o.price_gbp.toFixed(2)}</div>
            </div>
          ))}
          {offers.length === 0 && !result.error && <div className="text-xs opacity-60">No offers found.</div>}
        </div>
      </div>
    );
  }

  if (type === "directions") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-directions">
        {close}
        <div className={HEADLINE_STYLE}>// {result.mode?.toUpperCase()} DIRECTIONS</div>
        {result.error ? (
          <div className="text-red-400 text-xs">⚠ {result.error}. {result.where || ""}</div>
        ) : (
          <div className="space-y-3">
            <div className="border border-[var(--rc-gold-dim)] p-3 text-xs">
              <div className="opacity-70 mb-1">FROM</div>
              <div className="font-display">{result.from}</div>
            </div>
            <div className="border border-[var(--rc-gold-dim)] p-3 text-xs">
              <div className="opacity-70 mb-1">TO</div>
              <div className="font-display">{result.to}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">DISTANCE</div><div className="font-display text-lg rc-glow-text mt-1">{result.distance_km} km</div></div>
              <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">DURATION</div><div className="font-display text-lg rc-glow-text mt-1">{result.duration_min} min</div></div>
              <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">STEPS</div><div className="font-display text-lg mt-1">{result.steps_count}</div></div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (type === "find_recipe") {
    const recipes = result.results || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-3xl w-full" data-testid="stage-find_recipe">
        {close}
        <div className={HEADLINE_STYLE}>// RECIPES · {result.query}</div>
        {recipes.length === 0 && <div className="text-xs opacity-60">No recipes found.</div>}
        <div className="space-y-3 mt-2 max-h-[460px] overflow-y-auto pr-2">
          {recipes.map((r, i) => (
            <div key={i} className="border border-[var(--rc-gold-dim)] p-3" data-testid={`recipe-${i}`}>
              <div className="flex justify-between items-start mb-2">
                <div className="font-display text-base rc-glow-text">{r.name}</div>
                <div className="text-[10px] opacity-60">{r.area} · {r.category}</div>
              </div>
              <div className="rc-label text-[9px] mb-1">INGREDIENTS</div>
              <div className="text-[11px] mb-2">{r.ingredients.slice(0, 8).join(" · ")}{r.ingredients.length > 8 ? "…" : ""}</div>
              <div className="rc-label text-[9px] mb-1">METHOD</div>
              <div className="text-[10px] opacity-80">{r.instructions}…</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "simulate_trade") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-md w-full" data-testid="stage-simulate_trade">
        {close}
        <div className={HEADLINE_STYLE}>// 🧪 TRADE SIMULATOR</div>
        <div className="border border-[var(--rc-gold-dim)] p-3 text-sm">
          <div className="opacity-70 text-[10px] mb-1">SIMULATED · NO REAL ORDER PLACED</div>
          <div>{result.spoken}</div>
        </div>
      </div>
    );
  }

  if (type === "open_youtube" || type === "open_my_trader") {
    const url = result.url || "";
    const label = type === "open_my_trader" ? "RAW-DATA TRADER" : "YOUTUBE SEARCH";
    return (
      <div className="stage-in rc-panel relative p-5 max-w-md w-full" data-testid={`stage-${type}`}>
        {close}
        <div className={HEADLINE_STYLE}>// OPENING · {label}</div>
        <div className="border border-[var(--rc-gold-dim)] p-4 text-xs break-all mb-3">{url}</div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="rc-btn !text-xs !py-2 !px-4 inline-block"
          data-testid={`open-${type}-link`}
        >LAUNCH ↗</a>
        <div className="text-[10px] opacity-60 mt-3">If a new tab didn't open, tap LAUNCH ↗ above (some browsers block auto-popups).</div>
      </div>
    );
  }

  if (type === "raw_hub_dashboard") {
    return <RawHubDashboard dismiss={dismiss} />;
  }
  if (type === "capital_pnl") {
    // Use pre-fetched data from the action result (server.py executes the
    // capital_pnl handler now and returns the live numbers) so the panel
    // renders instantly without waiting for its own /api/jarvis/capital_pnl
    // round-trip.
    const initial = result && result.open_positions !== undefined ? result : null;
    return <CapitalPnLPanel dismiss={dismiss} initialData={initial} />;
  }
  if (type === "overnight") {
    return <OvernightPanel dismiss={dismiss} />;
  }
  if (type === "settings" || type === "diagnostics") {
    const initialTab = type === "diagnostics" ? "diag" : (action.tab || result.tab || "name");
    return <SettingsPanel dismiss={dismiss} initialTab={initialTab} />;
  }

  if (type === "tell_story" || type === "teach_something") {
    const label = type === "tell_story" ? "// STORY · LISTENING" : "// LEARN ONE THING";
    // Pull the actual narrative from the reply on the visual envelope (App.js
    // passes the whole {action, result, reply} into the visual prop).
    const story = visual.reply || result.reply || action.text || "";
    return (
      <div className={`stage-in rc-panel relative p-5 max-w-2xl w-full`} data-testid={`stage-${type}`}>
        {close}
        <div className={HEADLINE_STYLE}>{label}</div>
        <div className="border border-[var(--rc-gold-dim)] p-4 text-sm leading-relaxed text-[var(--rc-text)] whitespace-pre-wrap">
          {story || <span className="opacity-50 italic">{type === "tell_story" ? "Telling you a story now, sir." : "Teaching you a new thing now, sir."}</span>}
        </div>
      </div>
    );
  }

  if (type === "how_i_work") {
    const stack = result.stack || {};
    // The owner-journey numbers can arrive AFTER the panel mounts (the chat
    // endpoint streams text first, the action.result lands a beat later).
    // To stop the "spoken voice reads 000 because UI hasn't filled yet"
    // problem we look at result.* but ALSO eagerly re-poll /jarvis/journey
    // on mount so the stats are populated within ~200ms even if the
    // chat-attached action.result is empty.
    return (
      <div className="stage-in rc-panel relative p-5 max-w-5xl w-full" data-testid="stage-how_i_work">
        {close}
        <div className={HEADLINE_STYLE}>// UNDER THE HOOD · WIRING DIAGRAM</div>
        <HowItWorksStats fallback={result} />
        <div className="mt-4">
          <ArchitectureDiagram data={result?.diagram} onSend={onSend} />
        </div>
        <div className="rc-label text-[10px] mb-2 mt-4">STACK</div>
        <div className="space-y-1">
          {Object.entries(stack).map(([k, v]) => (
            <div key={k} className="flex justify-between text-xs border-l-2 border-[var(--rc-gold)] pl-3 py-1">
              <span className="rc-label text-[10px] opacity-80">{k.replace("_", " ")}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "morning_brief") {
    // Owner asked: "kill that big morning brief panel — it overlaps RAW
    // COMPANY and BBC News." The fan-out proof cards already show every
    // section of the brief (Capital, Open Positions, Raw Hub, BBC, Weather,
    // Calendar, Apps), so there's nothing left for this overlay to add.
    return null;
  }
  if (false && type === "morning_brief") {
    const msgs = result.messages || {};
    const iss = result.issues || {};
    const tr = result.trades || {};
    const fin = result.finance || {};
    const cap = result.capital || {};
    const rems = result.reminders_today || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-morning-brief">
        {close}
        <div className={HEADLINE_STYLE}>// MORNING BRIEF · {(result.label || "").toUpperCase()}</div>
        {cap.enabled && <BriefCapitalLiveStrip initialCap={cap} />}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="border border-[var(--rc-gold-dim)] p-3">
            <div className="rc-label text-[9px]">UNREAD MSGS</div>
            <div className="font-display text-base rc-glow-text mt-1 tabular-nums">{msgs.unread || 0}</div>
          </div>
          <div className="border border-[var(--rc-gold-dim)] p-3">
            <div className="rc-label text-[9px]">ISSUES</div>
            <div className={`font-display text-base mt-1 tabular-nums ${iss.needs_owner ? "text-red-400" : "rc-glow-text"}`}>{iss.needs_owner || 0}</div>
          </div>
          <div className="border border-[var(--rc-gold-dim)] p-3">
            <div className="rc-label text-[9px]">TRADES 24H</div>
            <div className={`font-display text-base mt-1 tabular-nums ${(tr.closed_pnl_gbp || 0) >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(tr.closed_pnl_gbp || 0).toFixed(2)}</div>
            <div className="text-[9px] text-[var(--rc-text-dim)] mt-1">{tr.opened_24h || 0} opened · {tr.closed_24h || 0} closed</div>
          </div>
          <div className="border border-[var(--rc-gold-dim)] p-3">
            <div className="rc-label text-[9px]">NET 24H</div>
            <div className={`font-display text-base mt-1 tabular-nums ${(fin.net_24h_gbp || 0) >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(fin.net_24h_gbp || 0).toFixed(2)}</div>
          </div>
        </div>
        {rems.length > 0 && (
          <>
            <div className="rc-label text-[10px] mb-2">REMINDERS TODAY</div>
            <div className="space-y-1">
              {rems.map((r, i) => (
                <div key={i} className="flex justify-between border-l-2 border-[var(--rc-gold)] pl-3 py-1 text-xs">
                  <span>{r.text}</span>
                  <span className="text-[var(--rc-text-dim)] tabular-nums text-[10px]">{r.trigger_at.slice(11, 16)}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {/* 🍒 Cherry on top — ASK THE SWARM about anything in the brief. The
            agents see a pre-built context summary so they don't ask "what
            do you mean by issues?" */}
        <div className="mt-4 pt-3 border-t border-[var(--rc-gold-dim)] flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[10px] opacity-70 italic">
            🍒 Want my agents' take on today's brief?
          </div>
          <button
            data-testid="brief-ask-swarm"
            onClick={() => {
              const ctx = `Here's my morning brief: ${msgs.unread || 0} unread messages, ${iss.needs_owner || 0} open issues, 24h trade P&L £${(tr.closed_pnl_gbp || 0).toFixed(2)}, net 24h £${(fin.net_24h_gbp || 0).toFixed(2)}, Capital balance £${(cap.balance_gbp || 0).toFixed(2)}. What should I focus on today and what can you fix for me?`;
              window.dispatchEvent(new CustomEvent("rawco-open-swarm", { detail: { message: ctx } }));
            }}
            className="rc-btn !text-[10px] !py-1.5 !px-3"
          >🍒 ASK THE SWARM</button>
        </div>
        {/* PROACTIVE — backend flagged red issues. Show a glowing banner and
            offer to AUTO-FIRE the swarm with the pre-built question. */}
        {result.proactive_swarm?.fire && (
          <ProactiveSwarmBanner
            severity={result.proactive_swarm.severity}
            flags={result.proactive_swarm.flags}
            question={result.proactive_swarm.question}
          />
        )}
      </div>
    );
  }

  if (type === "monthly_report") {
    const cats = result.top_categories || [];
    return (
      <div className="stage-in rc-panel relative p-5 max-w-2xl w-full" data-testid="stage-monthly-report">
        {close}
        <div className={HEADLINE_STYLE}>// MONTHLY · {(result.label || "").toUpperCase()}</div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">INCOME</div><div className="font-display text-base rc-glow-text mt-1 tabular-nums">£{(result.income_gbp || 0).toFixed(2)}</div></div>
          <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">OUTGOING</div><div className="font-display text-base mt-1 tabular-nums">£{(result.outgoing_gbp || 0).toFixed(2)}</div></div>
          <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">NET</div><div className={`font-display text-base mt-1 tabular-nums ${result.net_gbp >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(result.net_gbp || 0).toFixed(2)}</div></div>
          <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">TRADES</div><div className="font-display text-base mt-1 tabular-nums">{result.trades_count || 0}</div></div>
          <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">WIN / LOSS</div><div className="font-display text-base mt-1 tabular-nums">{result.trades_wins || 0} / {result.trades_losses || 0}</div></div>
          <div className="border border-[var(--rc-gold-dim)] p-3"><div className="rc-label text-[9px]">TRADE P&amp;L</div><div className={`font-display text-base mt-1 tabular-nums ${(result.trades_pnl_gbp || 0) >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(result.trades_pnl_gbp || 0).toFixed(2)}</div></div>
        </div>
        {cats.length > 0 && (
          <>
            <div className="rc-label text-[10px] mb-2">TOP CATEGORIES</div>
            <div className="space-y-1">
              {cats.map((c, i) => (
                <div key={i} className="flex justify-between border-l-2 border-[var(--rc-gold)] pl-3 py-1 text-xs">
                  <span>{c.category}</span>
                  <span className={`tabular-nums ${c.amount >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{c.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="text-[10px] text-[var(--rc-text-dim)] mt-3">Incidents: {result.incidents_logged || 0} · Dev escalations: {result.dev_escalations || 0}</div>
      </div>
    );
  }

  if (type === "report_problem" || type === "escalate_to_emergent") {
    return (
      <div className="stage-in rc-panel relative p-5 max-w-xl w-full" data-testid="stage-escalate">
        {close}
        <div className={HEADLINE_STYLE}>// {type === "escalate_to_emergent" ? "ESCALATED TO DEV" : "PROBLEM LOGGED"}</div>
        <div className="border border-[var(--rc-gold)] p-3">
          <div className="font-display text-base rc-glow-text">{action.title || result.title}</div>
          <div className="text-xs text-[var(--rc-text-dim)] mt-2">{action.description || action.detail}</div>
        </div>
      </div>
    );
  }

  return null;
}
